import { useEffect, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'axios'
import { ChevronLeft, Delete, Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'
import { posErrorText } from './ItemConfiguration'
import { FLAT, QUIET } from './posStyles'
import { posOrderingError } from './service'
import type { PosStaffPerson } from './types'

const PIN_LENGTH = 4
/** First letters of two names, or the start of a single one. */
export function initials(name: string) {
  const words = name.trim().split(/\s+/)
  return (words.length > 1 ? words[0][0] + words[1][0] : name.trim().slice(0, 2)).toUpperCase()
}

export interface PosStaffScreenProps {
  people: PosStaffPerson[]
  title: string
  backLabel: string
  onBack: () => void
  brand?: string
  /** Rejects with the server's answer for a wrong or locked PIN; the screen shows it and clears the digits. */
  onSubmit: (person: PosStaffPerson, pin: string) => Promise<void>
  summary?: ReactNode
  hint?: (person: PosStaffPerson) => string | undefined
  note?: (person: PosStaffPerson) => string | undefined
  forgotText?: string
}

/** The one name screen: logging in, switching, the idle lock, after the Tagesabschluss and manager approval. */
export function PosStaffScreen({ people, title, backLabel, onBack, brand, onSubmit, summary, hint, note, forgotText }: PosStaffScreenProps) {
  const { t } = useTranslation('posOrdering')
  const [selected, setSelected] = useState<PosStaffPerson | null>(people.length === 1 ? people[0] : null)
  const [pin, setPin] = useState(''), [busy, setBusy] = useState(false), [forgot, setForgot] = useState(false)
  const [problem, setProblem] = useState<{ text: string; detail?: string } | null>(null)
  const [lockedFor, setLockedFor] = useState(0)

  useEffect(() => {
    if (lockedFor <= 0) return
    const id = setTimeout(() => setLockedFor(left => left - 1), 1000)
    return () => clearTimeout(id)
  }, [lockedFor])

  async function submit(person: PosStaffPerson, value: string) {
    setBusy(true)
    try { await onSubmit(person, value) } catch (failure) {
      const data = axios.isAxiosError<{ remaining?: number; retryAfterSeconds?: number }>(failure) ? failure.response?.data : undefined
      const code = posOrderingError(failure).code
      setPin('')
      if (code === 'staff_locked') { setLockedFor(data?.retryAfterSeconds ?? 60); setProblem(null) }
      else if (code === 'wrong_pin') setProblem({ text: t('staff.wrongPin'), detail: t('staff.remaining', { count: data?.remaining ?? 0 }) })
      else setProblem({ text: posErrorText(t, code) })
    } finally { setBusy(false) }
  }
  function press(digit: string) {
    if (!selected || busy || lockedFor > 0 || pin.length >= PIN_LENGTH) return
    const next = pin + digit
    setPin(next); setProblem(null)
    if (next.length === PIN_LENGTH) void submit(selected, next)
  }
  useEffect(() => {
    const keys = (event: KeyboardEvent) => {
      if (/^[0-9]$/.test(event.key)) press(event.key)
      else if (event.key === 'Backspace') setPin(value => value.slice(0, -1))
    }
    window.addEventListener('keydown', keys)
    return () => window.removeEventListener('keydown', keys)
  })
  const choose = (person: PosStaffPerson) => { setSelected(person); setPin(''); setProblem(null); setLockedFor(0); setForgot(false) }
  const parkNote = selected ? note?.(selected) : undefined

  return <div role="dialog" aria-modal="true" aria-label={title} className="pos-print fixed inset-0 z-[60] flex flex-col overflow-y-auto bg-background text-foreground pos-tall:overflow-hidden pos-wide:overflow-hidden">
    <header className="flex h-[72px] shrink-0 items-center justify-between border-b bg-card pe-4 ps-2">
      <Button variant="ghost" className={cn(FLAT, 'h-12 gap-1.5 px-2 text-[17px] font-semibold hover:bg-secondary hover:text-foreground [&_svg]:size-[22px]')} onClick={onBack}><ChevronLeft className="rtl:rotate-180" />{backLabel}</Button>
      {brand && <span className="truncate text-base font-medium text-muted-foreground">{brand}</span>}
    </header>
    <div className="flex flex-1 flex-col pos-tall:min-h-0 pos-wide:min-h-0 pos-wide:flex-row">
      <section className="flex flex-col gap-3.5 p-3 pos-tall:min-h-0 pos-tall:flex-1 pos-tall:overflow-y-auto pos-wide:min-h-0 pos-wide:flex-1 pos-wide:overflow-y-auto">
        <div className="space-y-3 px-1 pt-3">
          {summary}
          <h1 className="font-menu text-[32px] font-normal leading-10">{title}</h1>
        </div>
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
          {people.map(person => {
            const chosen = selected?.id === person.id
            const line = hint?.(person)
            return <button key={person.id} type="button" aria-pressed={chosen} onClick={() => choose(person)}
              className={cn('flex h-24 min-w-0 items-center gap-3 rounded-xl bg-card px-3.5 text-start transition-transform duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.98] motion-reduce:transition-none', chosen ? 'ring-2 ring-primary' : 'shadow-pos')}>
              <span aria-hidden className={cn('flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] text-base font-bold', chosen ? 'bg-primary text-primary-foreground' : 'bg-secondary')}>{initials(person.name)}</span>
              <span className="min-w-0"><span className="block truncate text-lg font-semibold leading-6">{person.name}</span>
                <span className={cn('block truncate text-sm', line ? 'font-bold text-accent-foreground' : 'text-muted-foreground')}>{line ?? t(`staff.roles.${person.role}`)}</span></span>
            </button>
          })}
        </div>
      </section>
      <section className="flex shrink-0 flex-col items-center gap-3 rounded-t-[18px] bg-card px-3 pb-3 pt-6 shadow-pos-sheet pos-wide:w-[420px] pos-wide:justify-center pos-wide:rounded-none pos-wide:border-s pos-wide:shadow-none">
        {forgot ? <>
          <span className="text-xl font-semibold">{t('staff.forgot')}</span>
          <p className="max-w-72 text-center text-base">{forgotText ?? t('staff.forgotText')}</p>
          <Button variant="ghost" className={cn(QUIET, 'h-12 px-6 text-base')} onClick={() => setForgot(false)}>{t('staff.back')}</Button>
        </> : <>
          <h2 className="font-sans text-xl font-semibold">{selected ? t('staff.pinFor', { name: selected.name }) : t('staff.noneSelected')}</h2>
          <div className="flex h-[18px] items-center gap-[18px]" role="img" aria-label={t('staff.pinEntered', { count: pin.length })}>
            {Array.from({ length: PIN_LENGTH }, (_, index) => <span key={index}
              className={cn('h-[18px] w-[18px] rounded-full', problem ? 'bg-destructive' : index < pin.length ? 'bg-primary' : 'shadow-[inset_0_0_0_2px_hsl(var(--muted-foreground))]')} />)}
          </div>
          <div role="status" className="flex min-h-10 flex-col items-center justify-center text-center text-sm">
            {busy ? <Loader2 className="h-5 w-5 animate-spin" />
              : lockedFor > 0 ? <><strong>{t('staff.locked')}</strong><span className="text-muted-foreground">{t('staff.retryIn', { time: `${Math.floor(lockedFor / 60)}:${String(lockedFor % 60).padStart(2, '0')}` })}</span></>
                : problem ? <><strong className="text-destructive">{problem.text}</strong>{problem.detail && <span className="text-destructive">{problem.detail}</span>}</> : null}
          </div>
          <div className={cn('grid w-full grid-cols-3 gap-2', (!selected || lockedFor > 0) && 'opacity-40')}>
            {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map(digit => <Key key={digit} onClick={() => press(digit)}>{digit}</Key>)}
            <span />
            <Key onClick={() => press('0')}>0</Key>
            <Key label={t('staff.delete')} onClick={() => setPin(value => value.slice(0, -1))}><Delete className="h-[30px] w-[30px] rtl:rotate-180" /></Key>
          </div>
          <Button variant="ghost" className={cn(FLAT, 'h-[52px] text-base font-semibold text-muted-foreground hover:bg-transparent hover:text-foreground')} onClick={() => setForgot(true)}>{t('staff.forgot')}</Button>
          {parkNote && <p className="max-w-72 text-center text-sm text-muted-foreground">{parkNote}</p>}
        </>}
      </section>
    </div>
  </div>
}

function Key({ children, onClick, label }: { children: ReactNode; onClick: () => void; label?: string }) {
  return <button type="button" aria-label={label} onClick={onClick}
    className="flex h-16 items-center justify-center rounded-xl bg-secondary text-[30px] font-semibold transition-transform duration-100 hover:bg-secondary/70 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring active:scale-[0.97] motion-reduce:transition-none pos-tall:h-[112px] pos-wide:h-20">{children}</button>
}
